import type { OnboardingCase } from '../types/onboarding';
import { ONBOARDING_STATUS_LABELS, type OnboardingStatus } from '../types/onboarding';
import { formatDayMonYear } from './formatDate';
import { parseAddress } from './parseAddress';

/**
 * Build a flat list of `[label, value]` rows for the candidate detail
 * export. The shape mirrors what the detail page renders today
 * (summary → personal → bank → documents), so what the user sees in the
 * UI is what lands in the CSV.
 */
/**
 * Per-call overrides. Reserved as an object (rather than nothing) so the
 * helper signature stays stable as we add knobs (e.g. column subsets,
 * locale toggles) without churning every call site.
 */
export type CandidateExportOptions = Record<string, never>;

const empty = '';

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return empty;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const statusLabel = (state: OnboardingCase['state']): string => {
  return ONBOARDING_STATUS_LABELS[state as OnboardingStatus] ?? String(state ?? empty);
};

export function buildCandidateExportRows(
  candidate: OnboardingCase,
  _options: CandidateExportOptions = {},
): Array<[string, string]> {
  const c = candidate;
  const contact = c.contact ?? null;
  const job = c.job ?? null;
  const bank = c.bank_account ?? null;
  const docs = c.document_details ?? null;
  const address = parseAddress(c.address);

  const rows: Array<[string, string]> = [
    ['Case ID', String(c.id)],
    ['Status', statusLabel(c.state)],
    ['Created', formatDateTime(c.created_at)],
    ['Updated', formatDateTime(c.updated_at)],

    ['Name', contact?.name ?? empty],
    ['Email', contact?.email ?? empty],
    ['Phone', contact?.phone_number ?? empty],

    ['Job Title', job?.title ?? empty],
    ['Job Program', job?.program ?? empty],
    ['Job Department', job?.department ?? empty],
    ['Employment Type', job?.employment_type ?? empty],

    // Reporting Manager: prefer the resolved display name with email as
    // a fallback. Two adjacent rows so spreadsheets can sort / filter on
    // either piece independently without losing the other.
    ['Reporting Manager', c.reporting_manager_name?.trim() || c.reporting_manager || empty],
    ['Reporting Manager Email', c.reporting_manager ?? empty],
    ['Program Associate', c.assigned_program_associate ?? empty],
    ['PNC Officer', c.assigned_pnc_officer ?? empty],
    ['Rejection Reason', c.rejection_reason ?? empty],

    ['RGF ID', c.rgf_id ?? empty],
    ['Date of Birth', formatDayMonYear(c.date_of_birth)],
    ['Date of Joining', formatDayMonYear(c.joining_date)],
    ['Gender', c.gender ?? empty],
    ['Marital Status', c.marital_status ?? empty],
    ['Father / Husband Name', c.father_or_husband_name ?? empty],
    // Address is now four separately-filterable columns, parsed from
    // the comma-separated `address` string on the case.
    ['Village', address.village ?? empty],
    ['Block', address.block ?? empty],
    ['District', address.district ?? empty],
    ['PIN', address.pin ?? empty],
    ['PAN Card Number', docs?.pan_card_number ?? empty],
    ['Aadhaar Number', docs?.aadhaar_number ?? docs?.aadhaar_last4 ?? empty],

    ['Bank Account Holder Name', bank?.account_holder_name ?? empty],
    ['Bank Name', bank?.name ?? empty],
    ['Bank Account Number', bank?.account_number ?? bank?.account_number_last4 ?? empty],
    ['IFSC Code', bank?.ifsc_code ?? empty],
    ['Account Type', bank?.account_type ?? empty],
    ['Bank Address', bank?.bank_address ?? empty],
  ];

  return rows;
}

/**
 * Match any value that's purely digits and starts with a `0` (e.g. a bank
 * account number like `000405007899`). When Excel / Google Sheets open a
 * plain CSV, they coerce those cells to numbers and drop the leading
 * zeros — corrupting the data — even though the bytes on disk are correct.
 */
const LEADING_ZERO_DIGITS = /^0\d+$/;

/**
 * RFC-4180-ish CSV escape with one Excel-specific accommodation:
 *
 *   - Quote when the value contains a comma, quote, newline, or carriage
 *     return; double any internal quotes.
 *   - Emit leading-zero digit strings as the Excel/Sheets text-formula
 *     form `="000405007899"`. Both apps interpret that as a literal
 *     string and preserve the leading zeros on display, so account
 *     numbers, phone numbers with a leading 0, etc. survive the round
 *     trip into a spreadsheet.
 */
const escapeCsv = (value: string): string => {
  if (value === '') return '';
  if (LEADING_ZERO_DIGITS.test(value)) {
    return `="${value}"`;
  }
  if (/[,"\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export const rowsToKeyValueCsv = (rows: Array<[string, string]>): string => {
  const lines = [['Field', 'Value'], ...rows].map((row) =>
    row.map((cell) => escapeCsv(cell ?? '')).join(','),
  );
  return lines.join('\n');
};

const slug = (value: string | null | undefined, fallback: string): string => {
  if (!value) return fallback;
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || fallback
  );
};

export function downloadCandidateCsv(
  candidate: OnboardingCase,
  options: CandidateExportOptions = {},
): void {
  const rows = buildCandidateExportRows(candidate, options);
  // BOM so Excel opens unicode (e.g. names with non-ASCII chars) cleanly.
  const csv = '﻿' + rowsToKeyValueCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const name = slug(candidate.contact?.name ?? candidate.rgf_id, `case_${candidate.id}`);
  link.href = url;
  link.download = `candidate_${name}_${candidate.id}.csv`;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
